import { useEffect, useRef, useState } from 'react'

import './SingleSelect.scss'
import { useOnClickOutsideSelectors } from '../../Hooks/useOnClickOutsideSelectors'
import { useLocalization } from '../../Hooks/useLocalization'
import { isEmpty } from '../../Utils/Validations'

interface SingleSelectProps {
  items: Array<{ value: string; label: string }>
  value?: string
  onChange?: (newValue: string) => void
  label?: string
  helperText?: string
  isValid?: boolean
  isReadOnly?: boolean
  isRequired?: boolean
}

export function SingleSelect({
  items,
  value,
  onChange,
  label,
  helperText,
  isValid,
  isReadOnly,
  isRequired,
}: SingleSelectProps) {
  const localization = useLocalization('components')
  const [searchValue, setSearchValue] = useState<string>('')
  const [isShowListbox, setIsShowListbox] = useState(false)

  const listBoxRef = useRef<HTMLUListElement>(null)

  const handleSelect = (newValue: string) => {
    if (isReadOnly ?? false) return
    setSearchValue('')
    onChange?.(newValue)
    setIsShowListbox(false)
  }

  const searchedList = items.filter((item) =>
    item.label.toLowerCase().includes((searchValue ?? '').toLowerCase())
  )

  useOnClickOutsideSelectors(['.single-select-wrapper'], () => {
    setIsShowListbox(false)
  })

  useEffect(() => {
    const listBox = listBoxRef.current

    if (listBox) {
      setTimeout(() => {
        const rect = listBox.getBoundingClientRect()
        if (rect.bottom > window.innerHeight - 150) {
          listBox.classList.remove('bottom')
          listBox.classList.add('top')
        } else {
          listBox.classList.remove('top')
          listBox.classList.add('bottom')
        }
      }, 0)
    }
  }, [isShowListbox, searchedList])

  return (
    <div
      className={`single-select ${isValid === false ? 'invalid' : ''} ${
        isReadOnly ?? false ? 'is-read-only' : ''
      }`}
    >
      {!isEmpty(label) ? <div className="select-label">{label}</div> : null}
      <div className="single-select-wrapper">
        <input
          type="text"
          className={`select-input ${
            !searchValue && value === undefined ? ' no-value' : ''
          }`}
          placeholder={
            (value !== undefined
              ? items.find((i) => i.value === value)?.label
              : undefined) ??
            localization.text('select-from-list', 'Selecteer uit de lijst')
          }
          value={searchValue}
          onChange={(e) => {
            setSearchValue(e.target.value)
          }}
          onFocus={() => {
            if (isReadOnly ?? false) return
            setIsShowListbox(true)
          }}
        />
        {isShowListbox && searchedList.length > 0 && (
          <ul ref={listBoxRef} className="list-box">
            {isRequired !== true ? (
              <li
                key="empty-default"
                className="list-box-item"
                onClick={() => {
                  handleSelect('')
                }}
              />
            ) : (
              <></>
            )}
            {searchedList.map((item) => (
              <li
                key={item.value}
                className="list-box-item"
                onClick={() => {
                  handleSelect(item.value)
                }}
              >
                {item.label}
              </li>
            ))}
          </ul>
        )}
        {isShowListbox && searchedList.length === 0 && (
          <ul ref={listBoxRef} className="list-box">
            <li className="no-list-msg">
              {localization.text(
                'select-no-options-match-search',
                'Geen enkele optie komt overeen met de zoekterm'
              )}
            </li>
          </ul>
        )}
      </div>
      {!isEmpty(helperText) ? (
        <div className="select-helper-text">{helperText}</div>
      ) : null}
    </div>
  )
}
